import { useEffect, useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Building2,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Send,
  User,
  Video,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

import type { ConnectedCalendar } from '@/api/calendars'
import {
  AppointmentSlotUnavailableError,
  useCreateAppointment,
  useUpdateAppointment,
  type Appointment,
  type AppointmentCalendarProvider,
  type AppointmentType,
} from '@/api/appointments'
import type { AvailableNumber } from '@/types/agents'
import { ProviderBadge } from '../calendars/ProviderIcons'

import {
  APPOINTMENT_FORM_DEFAULTS,
  appointmentFormSchema,
  buildTimeOptions,
  DURATION_OPTIONS,
  normalizePhone,
  type AppointmentFormValues,
} from './formSchema'

// =============================================================================
// Helpers locaux — calculs de durée et formats date/heure.
// =============================================================================

function getDurationMinutes(startIso: string, endIso: string): number {
  return Math.round(
    (new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000,
  )
}

function isoToLocalDateAndTime(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

// =============================================================================
// Props
// =============================================================================

interface AppointmentFormProps {
  open: boolean
  onClose: () => void
  companyId: number | undefined
  editing: Appointment | null
  calendars: ConnectedCalendar[]
  smsNumbers: AvailableNumber[]
}

// =============================================================================
// Composant
// =============================================================================

export function AppointmentForm({
  open,
  onClose,
  companyId,
  editing,
  calendars,
  smsNumbers,
}: AppointmentFormProps) {
  const { t } = useTranslation()
  const createMutation = useCreateAppointment(companyId)
  const updateMutation = useUpdateAppointment(companyId)
  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const timeOptions = useMemo(() => buildTimeOptions(), [])

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: APPOINTMENT_FORM_DEFAULTS,
  })
  const { control, register, handleSubmit, reset, formState, setValue } = form

  // Hydrate à l'ouverture (create vs edit). Le reset est piloté par `open`
  // pour réinitialiser proprement le state à chaque ouverture.
  useEffect(() => {
    if (!open) return

    if (editing) {
      const { date, time } = isoToLocalDateAndTime(editing.start_time)
      const provider = editing.google_calendar
        ? 'google'
        : editing.square_booking
          ? 'square'
          : editing.wix_booking
            ? 'wix'
            : 'microsoft'
      const calId =
        editing.google_calendar ?? editing.square_booking ?? editing.wix_booking ?? editing.microsoft_calendar ?? ''
      reset({
        calendar_selection: calId ? `${provider}:${calId}` : '',
        client_name: editing.client_name,
        client_phone: editing.client_phone,
        client_email: editing.client_email ?? '',
        start_date: date,
        start_time: time,
        duration: getDurationMinutes(editing.start_time, editing.end_time),
        notes: editing.notes ?? '',
        appointment_type: editing.appointment_type ?? 'phone',
        location: editing.location ?? '',
        send_sms: false,
        sms_from_number: smsNumbers[0]?.phone_number ?? '',
        send_email: false,
        send_calendar_invite: false,
      })
    } else {
      const defaultCal = calendars[0]
      reset({
        ...APPOINTMENT_FORM_DEFAULTS,
        calendar_selection: defaultCal
          ? `${defaultCal.provider}:${defaultCal.id}`
          : '',
        start_date: todayIso(),
        sms_from_number: smsNumbers[0]?.phone_number ?? '',
      })
    }
  }, [open, editing, calendars, smsNumbers, reset])

  const appointmentType = useWatch({ control, name: 'appointment_type' })
  const sendSms = useWatch({ control, name: 'send_sms' })
  const clientEmail = useWatch({ control, name: 'client_email' })

  const onSubmit = async (values: AppointmentFormValues) => {
    const startDateTime = `${values.start_date}T${values.start_time}:00`
    // Le schéma a déjà rejeté les numéros non-normalisables ; on prend la
    // version E.164 ici pour envoyer un format propre au backend (Twilio).
    const normalizedPhone = normalizePhone(values.client_phone) ?? values.client_phone

    if (editing) {
      try {
        await updateMutation.mutateAsync({
          appointmentId: editing.id,
          patch: {
            client_name: values.client_name,
            client_phone: normalizedPhone,
            client_email: values.client_email || undefined,
            notes: values.notes,
            appointment_type: values.appointment_type,
            location: values.location,
            start_time: startDateTime,
            duration: values.duration,
          },
        })
        toast.success(t('appointments.updateSuccess'))
        onClose()
      } catch {
        toast.error(t('appointments.error'))
      }
      return
    }

    // Création : résoudre provider/id depuis la sélection encodée.
    const [provider, idRaw] = values.calendar_selection.split(':')
    const calendarId = Number(idRaw)
    if (!provider || !calendarId) {
      toast.error(t('appointments.selectCalendar'))
      return
    }

    try {
      const result = await createMutation.mutateAsync({
        calendar_id: calendarId,
        calendar_provider: provider as AppointmentCalendarProvider,
        client_name: values.client_name,
        client_phone: normalizedPhone,
        client_email: values.client_email || undefined,
        start_time: startDateTime,
        duration: values.duration,
        notes: values.notes,
        appointment_type: values.appointment_type,
        location: values.location || undefined,
        send_sms: values.send_sms,
        sms_from_number:
          values.send_sms && values.sms_from_number
            ? values.sms_from_number
            : undefined,
        send_email: values.send_email,
        send_calendar_invite: values.send_calendar_invite,
      })
      toast.success(t('appointments.createSuccess'))
      // Backend renvoie un warning quand le lien visio n'a pas pu être généré.
      if (result.warning === 'video_link_unavailable') {
        toast.warning(t('appointments.videoLinkUnavailable'))
      }
      onClose()
    } catch (err) {
      if (err instanceof AppointmentSlotUnavailableError) {
        toast.error(t('appointments.slotUnavailableTitle'), {
          description: t('appointments.slotUnavailable'),
        })
      } else {
        toast.error(t('appointments.error'))
      }
    }
  }

  // Validation visuelle minimale (sans bloquer le submit RHF — il valide aussi).
  const calendarOptions = useMemo(
    () =>
      calendars.map((cal) => ({
        value: `${cal.provider}:${cal.id}`,
        provider: cal.provider,
        calendar_name: cal.calendar_name,
        email: cal.email,
      })),
    [calendars],
  )

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[480px] p-0 flex flex-col gap-0"
      >
        <SheetHeader className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <SheetTitle className="text-xl font-bold tracking-tight">
            {editing
              ? t('appointments.editTitle')
              : t('appointments.createTitle')}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {editing
              ? t('appointments.editTitle')
              : t('appointments.createTitle')}
          </SheetDescription>
        </SheetHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Calendrier (création uniquement — pas modifiable à l'édition) */}
            {!editing && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {t('appointments.formCalendar')}
                </Label>
                <Controller
                  control={control}
                  name="calendar_selection"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('appointments.selectCalendar')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {calendarOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="inline-flex items-center gap-2">
                              <ProviderBadge provider={opt.provider} />
                              <span>{opt.calendar_name}</span>
                              <span className="text-slate-400">
                                - {opt.email}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {formState.errors.calendar_selection && (
                  <p className="text-xs text-red-500">
                    {t('appointments.selectCalendar')}
                  </p>
                )}
              </div>
            )}

            {/* Type de rendez-vous */}
            {!editing && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('appointments.sectionType')}
                  </h3>
                </div>
                <Controller
                  control={control}
                  name="appointment_type"
                  render={({ field }) => (
                    <div className="grid grid-cols-3 gap-2">
                      {(
                        [
                          {
                            value: 'phone',
                            Icon: Phone,
                            label: t('appointments.type_phone'),
                            active:
                              'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
                          },
                          {
                            value: 'in_person',
                            Icon: Building2,
                            label: t('appointments.type_in_person'),
                            active:
                              'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
                          },
                          {
                            value: 'video',
                            Icon: Video,
                            label: t('appointments.type_video'),
                            active:
                              'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
                          },
                        ] satisfies {
                          value: AppointmentType
                          Icon: typeof Phone
                          label: string
                          active: string
                        }[]
                      ).map(({ value, Icon, label, active }) => {
                        const isActive = field.value === value
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => field.onChange(value)}
                            className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                              isActive
                                ? active
                                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                          >
                            <Icon className="h-5 w-5" />
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                />

                {appointmentType === 'in_person' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('appointments.formLocation')}
                    </Label>
                    <Input
                      placeholder={t('appointments.formLocationPlaceholder')}
                      {...register('location')}
                    />
                  </div>
                )}

                {appointmentType === 'video' && (
                  <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <Video className="h-4 w-4 text-purple-600 dark:text-purple-400 shrink-0" />
                    <p className="text-xs text-purple-700 dark:text-purple-300">
                      {t('appointments.videoAutoGenerated')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Section client */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                <User className="h-3.5 w-3.5 text-slate-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {t('appointments.sectionClient')}
                </h3>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('appointments.formClientName')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input placeholder="Jean Dupont" {...register('client_name')} />
                {formState.errors.client_name && (
                  <p className="text-xs text-red-500">
                    {t('appointments.requiredFields')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('appointments.formPhone')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="tel"
                  placeholder="+1 514 555-1234"
                  {...register('client_phone')}
                />
                {formState.errors.client_phone && (
                  <p className="text-xs text-red-500">
                    {formState.errors.client_phone.message === 'invalid_phone'
                      ? t('appointments.invalidPhone')
                      : t('appointments.requiredFields')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {appointmentType === 'video'
                    ? t('appointments.formEmailRequired')
                    : t('appointments.formEmail')}
                  {appointmentType === 'video' && (
                    <span className="text-red-500"> *</span>
                  )}
                </Label>
                <Input
                  type="email"
                  placeholder="jean@exemple.com"
                  {...register('client_email')}
                />
                {formState.errors.client_email && (
                  <p className="text-xs text-red-500">
                    {appointmentType === 'video'
                      ? t('appointments.emailRequiredForVideo')
                      : t('appointments.requiredFields')}
                  </p>
                )}
              </div>
            </div>

            {/* Section date/heure */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {t('appointments.sectionDateTime')}
                </h3>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('appointments.formDate')}{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <Input type="date" {...register('start_date')} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('appointments.formTime')}{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="start_time"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('appointments.formDuration')}
                  </Label>
                  <Controller
                    control={control}
                    name="duration"
                    render={({ field }) => (
                      <Select
                        value={String(field.value)}
                        onValueChange={(v) => field.onChange(Number(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DURATION_OPTIONS.map((opt) => (
                            <SelectItem
                              key={opt.value}
                              value={String(opt.value)}
                            >
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('appointments.formNotes')}
              </Label>
              <Textarea
                rows={3}
                placeholder={t('appointments.formNotesPlaceholder')}
                {...register('notes')}
              />
            </div>

            {/* Notifications (création uniquement) */}
            {!editing && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                  <Send className="h-3.5 w-3.5 text-slate-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t('appointments.sectionNotifications')}
                  </h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                      <Controller
                        control={control}
                        name="send_sms"
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onCheckedChange={(v) => {
                              field.onChange(v)
                              // Pré-sélectionne le premier numéro si l'utilisateur
                              // active la notif SMS sans en avoir choisi un.
                              if (v && !form.getValues('sms_from_number') && smsNumbers[0]) {
                                setValue('sms_from_number', smsNumbers[0].phone_number)
                              }
                            }}
                          />
                        )}
                      />
                      <MessageSquare className="h-4 w-4 text-slate-400" />
                      {t('appointments.notifSms')}
                    </Label>
                    {sendSms && smsNumbers.length > 1 && (
                      <Controller
                        control={control}
                        name="sms_from_number"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="h-8 text-xs w-auto min-w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {smsNumbers.map((n) => (
                                <SelectItem
                                  key={n.id}
                                  value={n.phone_number}
                                >
                                  via {n.label ? `${n.label} (${n.phone_number})` : n.phone_number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    )}
                  </div>

                  <Label
                    className={`flex items-center gap-2 text-sm ${
                      clientEmail
                        ? 'text-slate-700 dark:text-slate-300 cursor-pointer'
                        : 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                    }`}
                  >
                    <Controller
                      control={control}
                      name="send_email"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          disabled={!clientEmail}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                    <Mail className="h-4 w-4 text-slate-400" />
                    {t('appointments.notifEmail')}
                  </Label>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 px-6 py-4 flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('appointments.formCancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editing ? (
                t('appointments.formSave')
              ) : (
                t('appointments.formCreate')
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
